/**
 * Evaluator scoring engine for STF scan points.
 *
 * Evaluators are named configurations with rules (metric + threshold)
 * and a mode that determines how multiple rules combine:
 *   - "worst" : score = max of all per-rule severity values
 *   - "any"   : pass/fail — fails if ANY rule exceeds its threshold
 *   - "all"   : pass/fail — fails only if ALL rules exceed their thresholds
 *
 * The per-rule severity uses the same non-linear power curve as severityScore.js:
 *   severity = (value / threshold) ^ EXPONENT
 * so values well under budget barely register while approaching/exceeding
 * budget ramps steeply.
 */

const SEVERITY_EXPONENT = 2.2;

function severityCurve(ratio) {
  if (ratio <= 0) return 0;
  return Math.pow(ratio, SEVERITY_EXPONENT);
}

/**
 * Compute per-rule severity for a single metric.
 * Uses the rule's high value as the threshold.
 * @param {number} value - The metric value from the capture point
 * @param {number} threshold - The budget threshold (rule.high or rule.threshold)
 * @returns {number} Severity score (0 = fine, 1 = at threshold, >1 = over)
 */
export function ruleSeverity(value, threshold) {
  if (value == null || !threshold) return 0;
  // For inverted metrics (e.g. FPS where lower is worse), use absolute ratio
  return severityCurve(Math.abs(value / threshold));
}

/**
 * Evaluate a single capture point against an evaluator config.
 *
 * @param {object} point - Capture point with metric properties
 * @param {object} evaluator - { mode, rules: [{ metric, threshold }] }
 * @returns {{ score: number, exceeded: boolean, ruleResults: Array }}
 *   score     — composite severity (0+ for worst mode, 0 or 1 for any/all)
 *   exceeded  — true if the point fails the evaluator's threshold test
 *   ruleResults — per-rule breakdown: [{ metric, threshold, value, severity, exceeded }]
 */
export function evaluatePoint(point, evaluator) {
  if (!point || !evaluator?.rules?.length) {
    return { score: 0, exceeded: false, ruleResults: [] };
  }

  const ruleResults = evaluator.rules.map(rule => {
    const value = point[rule.metric];
    const threshold = rule.threshold ?? rule.high;
    const severity = ruleSeverity(value, threshold);
    return {
      metric: rule.metric,
      threshold,
      value: value ?? null,
      severity,
      exceeded: severity >= 1.0
    };
  });

  const validResults = ruleResults.filter(r => r.value != null);
  if (validResults.length === 0) {
    return { score: 0, exceeded: false, ruleResults };
  }

  let score = 0;
  let exceeded = false;

  switch (evaluator.mode) {
    case 'worst':
      // Score = maximum severity across all rules
      score = Math.max(...validResults.map(r => r.severity));
      exceeded = score >= 1.0;
      break;

    case 'any':
      // Fails if ANY rule exceeds its threshold
      exceeded = validResults.some(r => r.exceeded);
      // For coloring, use the max severity so we get a gradient
      score = Math.max(...validResults.map(r => r.severity));
      break;

    case 'all':
      // Fails only if ALL rules exceed their thresholds
      exceeded = validResults.every(r => r.exceeded);
      // For coloring, use the minimum severity (weakest link)
      score = Math.min(...validResults.map(r => r.severity));
      break;

    default:
      score = Math.max(...validResults.map(r => r.severity));
      exceeded = score >= 1.0;
  }

  return { score, exceeded, ruleResults };
}

/**
 * Map evaluator score to the 20-45 "FPS equivalent" scale used by
 * HeatmapViewer's color pipeline.
 *
 * score 0.0  → 45 (green)
 * score 1.0  → 20 (red)
 * score 1.5+ → 15 (deep red)
 */
export function evaluatorScoreToFps(score) {
  if (score == null) return null;
  return Math.max(15, Math.min(45, 45 - score * 25));
}

/**
 * Available metrics that can be used in evaluator rules.
 * Each entry maps a metric key to its display label and the
 * property name on capture points.
 */
export const AVAILABLE_METRICS = [
  { key: 'framerate',                 label: 'Framerate (FPS)',              defaultLow: 45,     defaultHigh: 20 },
  { key: 'triangles',                 label: 'Triangles',                    defaultLow: 250000, defaultHigh: 500000 },
  { key: 'vertices',                  label: 'Vertices',                     defaultLow: 375000, defaultHigh: 750000 },
  { key: 'drawCalls',                 label: 'Draw Calls',                   defaultLow: 600,    defaultHigh: 1200 },
  { key: 'batches',                   label: 'Batches',                      defaultLow: 600,    defaultHigh: 1200 },
  { key: 'setPassCalls',              label: 'SetPass Calls',                defaultLow: 90,     defaultHigh: 180 },
  { key: 'visibleRenderers',          label: 'Visible Renderers',            defaultLow: 200,    defaultHigh: 400 },
  { key: 'candidateRenderers',        label: 'Renderer Density',             defaultLow: 300,    defaultHigh: 600 },
  { key: 'candidateTriangles',        label: 'Triangle Density',             defaultLow: 400000, defaultHigh: 800000 },
  { key: 'cullingRatio',              label: 'Culling Ratio',                defaultLow: 0.3,    defaultHigh: 0.7 },
  { key: 'shadowCasterRenderers',     label: 'Shadow Casters',               defaultLow: 50,     defaultHigh: 100 },
  { key: 'candidateLights',          label: 'Lights in View',               defaultLow: 12,     defaultHigh: 25 },
  { key: 'candidateShadowCastingLights', label: 'Shadow-Casting Lights',   defaultLow: 4,      defaultHigh: 8 },
  { key: 'transparentDraws',          label: 'Transparent Draws',            defaultLow: 100,    defaultHigh: 200 },
  { key: 'opaqueDraws',              label: 'Opaque Draws',                 defaultLow: 400,    defaultHigh: 800 },
  { key: 'depthPrepassDraws',        label: 'Depth Prepass Draws',          defaultLow: 200,    defaultHigh: 400 },
  { key: 'mainLightShadowDraws',     label: 'Main Light Shadow Draws',      defaultLow: 150,    defaultHigh: 300 },
  { key: 'additionalLightShadowDraws', label: 'Additional Light Shadow Draws', defaultLow: 50,  defaultHigh: 100 },
  { key: 'batchedDraws',             label: 'Batched Draws',                defaultLow: 400,    defaultHigh: 800 },
  { key: 'unbatchedDraws',           label: 'Unbatched Draws',              defaultLow: 200,    defaultHigh: 400 },
  { key: 'distinctShaderVariants',    label: 'Shader Variants',              defaultLow: 50,     defaultHigh: 100 },
];

/**
 * Compute per-rule badness for heatmap coloring (0 = green, 1 = red).
 * Uses the rule's low (good) / high (bad) thresholds with linear interpolation.
 * Supports inversion: if low > high (e.g. FPS: low=45, high=20), lower values are worse.
 */
export function ruleBadness(value, rule) {
  if (value == null) return 0;
  const low = rule.low ?? 0;
  const high = rule.high ?? rule.threshold;
  if (high === low) return 0;
  // Works for both directions: (value - low) / (high - low) naturally inverts when low > high
  return Math.max(0, Math.min(1, (value - low) / (high - low)));
}

/**
 * Evaluate a point's heatmap badness (0-1) using per-rule low/high thresholds.
 * Combines rules using the same mode logic as evaluatePoint.
 */
export function evaluateBadness(point, evaluator) {
  if (!point || !evaluator?.rules?.length) return 0;

  const results = evaluator.rules
    .map(rule => ({ value: point[rule.metric], rule }))
    .filter(r => r.value != null);

  if (results.length === 0) return 0.5; // No data

  const badnesses = results.map(r => ruleBadness(r.value, r.rule));

  switch (evaluator.mode) {
    case 'worst':
    case 'any':
      return Math.max(...badnesses);
    case 'all':
      return Math.min(...badnesses);
    default:
      return Math.max(...badnesses);
  }
}

/**
 * Create a default evaluator config (useful for "New Evaluator" UI).
 */
export function defaultEvaluator() {
  return {
    name: '',
    mode: 'worst',
    rules: [
      { metric: 'triangles', low: 250000, high: 500000 },
      { metric: 'visibleRenderers', low: 200, high: 400 },
    ]
  };
}
